import React, { Fragment, useState } from 'react'
import { useDispatch } from 'react-redux'
import { PencilIcon, PlusIcon, QueueListIcon } from '@heroicons/react/24/outline'
import { Menu, Transition } from '@headlessui/react'
import { EllipsisVerticalIcon } from '@heroicons/react/20/solid'
import RenameSectionForm from '../rename-section-form/RenameSectionForm'
import { classNames } from '../../../../../../../utils/className'
import { changeAssignment, deleteDynamicTableColumn } from '../../../../../../../reducers/meeting-type-task/constructor'
import { openForm as openAddColumnForm } from '../../../../../../../reducers/meeting-type-task/dynamic-table/add-column'
import { openForm as openUpdateColumnForm } from '../../../../../../../reducers/meeting-type-task/dynamic-table/update-column'

export default function DynamicTableSection({
  section,
  index,
  saveContent,
  deleteSection,
  renamingSectionUuid,
  setRenamingSectionUuid,
}) {
  const dispatch = useDispatch()

  const changeAssignmentProcess = (event, index) => {
    dispatch(changeAssignment({ index, value: event.target.value }))
  }

  const openAddColumnFormProcess = () => {
    dispatch(
      openAddColumnForm({
        sectionIndex: index,
      }),
    )
  }

  const openUpdateColumnFormProcess = (event, columnIndex) => {
    event.preventDefault()

    const column = section.data.cols[columnIndex]
    dispatch(
      openUpdateColumnForm({
        sectionIndex: index,
        columnIndex,
        name: column.name,
        description: column.description,
      }),
    )
  }

  const deleteColProcess = (event, indexForDelete) => {
    event.preventDefault()
    dispatch(deleteDynamicTableColumn({ sectionIndex: index, indexForDelete: indexForDelete }))
  }

  return (
    <div>
      <div className={'mb-4 flex flex-row justify-between'}>
        <div className={'flex items-center text-sm text-gray-500'}>
          <QueueListIcon className={'w-4 h-4 text-gray-500 mr-4'} />
          {renamingSectionUuid === section.uuid && (
            <RenameSectionForm section={section} sectionIndex={index} saveContent={saveContent} />
          )}
          {renamingSectionUuid !== section.uuid && (
            <>
              {section.name || 'Dynamic table section'}{' '}
              <button
                type={'button'}
                onClick={(event) => {
                  setRenamingSectionUuid(section.uuid)
                }}
                className={'ml-1 p-1 rounded-md hover:bg-indigo-50'}
              >
                <PencilIcon className={'w-4 h-4 text-gray-400'} />
              </button>
              <span className={'pl-8 text-gray-300 text-sm'}>{section.uuid}</span>
            </>
          )}
        </div>
        <div>
          <Menu as="div" className="relative flex-none">
            <Menu.Button className="-m-2.5 block p-2.5 text-gray-500 hover:text-gray-900">
              <span className="sr-only">Open options</span>
              <EllipsisVerticalIcon className="h-5 w-5" aria-hidden="true" />
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none">
                <Menu.Item>
                  {({ focus }) => (
                    <button
                      onClick={() => {
                        deleteSection(index)
                      }}
                      className={classNames(
                        focus ? 'bg-gray-50' : '',
                        'block w-full text-left px-3 py-1 text-sm leading-6 text-gray-900',
                      )}
                    >
                      Delete section
                    </button>
                  )}
                </Menu.Item>
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      </div>
      <textarea
        placeholder={'Please provide an assignment for the AI just here...'}
        onChange={(event) => {
          changeAssignmentProcess(event, index)
        }}
        rows={8}
        value={section.data.assignment || section.task}
        className={'w-full rounded-md border-0 ring-1 text-sm text-gray-700'}
        name={`section-${index}-task`}
      />
      <div className={'space-y-4'}>
        <div className={'text-sm text-gray-500 pt-8'}>Columns in the table</div>
        <div className={'flex flex-row'}>
          <div
            className={classNames(
              `grid gap-2`,
              section.data.cols.length > 0 && `w-full mr-4`,
              section.data.cols.length === 1 && `grid-cols-1`,
              section.data.cols.length === 2 && `grid-cols-2`,
              section.data.cols.length === 3 && `grid-cols-3`,
              section.data.cols.length === 4 && `grid-cols-4`,
              section.data.cols.length === 5 && `grid-cols-5`,
              section.data.cols.length === 6 && `grid-cols-6`,
            )}
          >
            {section.data.cols.map((col, colIndex) => (
              <div key={`column-${colIndex}`} className={'flex flex-row'}>
                <div
                  className={
                    'text-left cursor-pointer w-full flex justify-between items-center col-span-1 rounded-md p-2 py-1 bg-white ring-1 ring-gray-200 text-sm text-gray-700 hover:bg-indigo-50 transition-all'
                  }
                >
                  <div
                    className={'line-clamp-3 overflow-hidden'}
                    onClick={(event) => {
                      openUpdateColumnFormProcess(event, colIndex)
                    }}
                  >
                    <div className={'text-sm text-gray-700 font-semibold'}>{col.name}</div>
                    {col.description ?? <div className={'text-gray-400'}>No description for this col</div>}
                  </div>
                  <Menu
                    as="div"
                    key={`col-options-${colIndex}-${(Math.random() + 1).toString(36).substring(7)}`}
                    className="relative flex-none"
                  >
                    <Menu.Button className="-m-2.5 block p-2.5 text-gray-500 hover:text-gray-900">
                      <span className="sr-only">Open options</span>
                      <EllipsisVerticalIcon className="h-5 w-5" aria-hidden="true" />
                    </Menu.Button>
                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none">
                        <Menu.Item>
                          {({ focus }) => (
                            <button
                              onClick={(event) => {
                                openUpdateColumnFormProcess(event, colIndex)
                              }}
                              className={classNames(
                                focus ? 'bg-gray-50' : '',
                                'block w-full text-left px-3 py-1 text-sm leading-6 text-gray-900',
                              )}
                            >
                              Update column
                            </button>
                          )}
                        </Menu.Item>
                        <Menu.Item>
                          {({ focus }) => (
                            <button
                              onClick={(event) => {
                                deleteColProcess(event, colIndex)
                              }}
                              className={classNames(
                                focus ? 'bg-gray-50' : '',
                                'block w-full text-left px-3 py-1 text-sm leading-6 text-gray-900',
                              )}
                            >
                              Delete column
                            </button>
                          )}
                        </Menu.Item>
                      </Menu.Items>
                    </Transition>
                  </Menu>
                </div>
              </div>
            ))}
          </div>
          {section.data.cols.length < 6 && (
            <button
              type={'button'}
              onClick={openAddColumnFormProcess}
              className={classNames(
                'flex text-sm text-gray-500 flex-row items-center px-4 py-2 hover:bg-indigo-50 transition-all ring-1 ring-gray-200 rounded-md bg-gray-50',
              )}
            >
              <PlusIcon className={classNames('w-5 h-5 text-gray-700', section.data.cols.length === 0 && 'mr-2')} />
              {section.data.cols.length === 0 && 'Add new column'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
